import { NamedModelsFilterParams, NamedModel } from 'api/Exec/types';
import TableTopPanel from 'app/common_components/TableTopPanel/TableTopPanel';
import usePaginationParams from 'app/hooks/usePaginationParams';
import DetailedNamedModel from 'components/Dashboard/Models/NamedModels/DetailedNamedModel/DetailedNamedModel';
import NamedModelForm from 'components/Dashboard/Models/NamedModels/NamedModelForm/NamedModelForm';
import NamedModelHistory from 'components/Dashboard/Models/NamedModels/NamedModelHistory/NamedModelHistory';
import NamedModelsFilter from 'components/Dashboard/Models/NamedModels/NamedModelsFilter/NamedModelsFilter';
import { useNamedModels } from 'components/Dashboard/Orgs/Wizard/OrgModels/hooks';
import { DashboardPageProps } from 'components/Dashboard/types';
import useColumns from 'components/Dashboard/Models/NamedModels/hooks';

import React, { useCallback, useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Table, Typography } from 'antd';

const NamedModels: React.FC<DashboardPageProps> = ({ setTitle }) => {
  const { isLoading, namedModels, fetchNamedModels, setNamedModels } = useNamedModels();
  const {
    searchParam,
    modelApiParam,
    imageTagParam,
    isSandboxParam,
    isCvParam,
    isEnabledParam,
  } = usePaginationParams();

  const getFilter = useCallback((): NamedModelsFilterParams => ({
    search: searchParam,
    modelApiNames: modelApiParam.split(','),
    imageTag: imageTagParam,
    isSandbox: isSandboxParam,
    isCv: isCvParam,
    isEnabled: isEnabledParam,
  }), [
    searchParam,
    modelApiParam,
    imageTagParam,
    isSandboxParam,
    isCvParam,
    isEnabledParam,
  ]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchNamedModels(getFilter(), abortController);

    return () => {
      abortController.abort();
    };
  }, [ fetchNamedModels, getFilter ]);

  const [ isDetailedModelOpen, setIsDetailedModelOpen ] = useState<NamedModel | null>(null);
  const openDetailedModel = (model: NamedModel) => setIsDetailedModelOpen(model);
  const closeDetailedModel = () => setIsDetailedModelOpen(null);

  const [ isModelHistoryOpen, setIsModelHistoryOpen ] = useState<NamedModel | null>(null);
  const opeModelHistory = (model: NamedModel) => setIsModelHistoryOpen(model);
  const closeModelHistory = () => setIsModelHistoryOpen(null);

  const [ namedModelToEdit, setNamedModelToEdit ] = useState<NamedModel | null>(null);
  const [ isAddNamedModelModalOpen, setIsAddNamedModelModalOpen ] = useState<boolean>(false);
  const openAddNamedModelModal = (namedModelId: NamedModel | null) => {
    setIsAddNamedModelModalOpen(true);
    setNamedModelToEdit(namedModelId);
  };
  const closeNamedModelModal = () => {
    setIsAddNamedModelModalOpen(false);
    setNamedModelToEdit(null);
  };

  const { columns } = useColumns(openDetailedModel, opeModelHistory, openAddNamedModelModal);

  const addNamedModelAndCloseModal = (namedModel: NamedModel) => {
    setNamedModels((prevState: NamedModel[]) => [ namedModel, ...prevState ]);
    closeNamedModelModal();
  };

  const updateNamedModelAndCloseModal = (updatedNamedModel: NamedModel) => {
    const namedModelIndex = namedModels.findIndex((namedModel: NamedModel) => namedModel.id === updatedNamedModel.id);
    setNamedModels([
      ...namedModels.slice(0, namedModelIndex),
      updatedNamedModel,
      ...namedModels.slice(namedModelIndex + 1, namedModels.length),
    ]);

    closeNamedModelModal();
  };

  useEffect(() => {
    setTitle('Named models');
  }, [ setTitle ]);

  return (
    <>
      <TableTopPanel
        Filter={NamedModelsFilter}
        RightColumn={<Button type="primary" onClick={() => openAddNamedModelModal(null)} icon={<PlusOutlined />}>Add named model</Button>}
        placeholder="Search by model name"
      />

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={namedModels}
        pagination={false}
        scroll={{ x: true }}
        loading={isLoading}
      />

      <Modal
        title={<Typography.Title level={4}>Named model details</Typography.Title>}
        open={!!isDetailedModelOpen}
        footer={null}
        width={1000}
        onCancel={closeDetailedModel}
        destroyOnClose
      >
        <DetailedNamedModel model={isDetailedModelOpen} />
      </Modal>

      <Modal
        title={<Typography.Title level={4}>Named model history</Typography.Title>}
        open={!!isModelHistoryOpen}
        footer={null}
        width={1280}
        onCancel={closeModelHistory}
        destroyOnClose
      >
        <NamedModelHistory model={isModelHistoryOpen} />
      </Modal>

      <Modal
        title={<Typography.Title level={4}>{namedModelToEdit ? 'Edit' : 'Add'} named model</Typography.Title>}
        open={isAddNamedModelModalOpen}
        footer={null}
        width={1280}
        onCancel={closeNamedModelModal}
        destroyOnClose
      >
        <NamedModelForm
          onCancel={closeNamedModelModal}
          onNamedModelAdded={addNamedModelAndCloseModal}
          onNamedModelUpdated={updateNamedModelAndCloseModal}
          namedModel={namedModelToEdit}
        />
      </Modal>
    </>
  );
};

export default NamedModels;
